/**
 * WHAT IS ACTUALLY MISSING FROM THE DESIGN FEED — pure, no imports.
 *
 * A picker that says "no designs in this folder" when the truth is "nobody has
 * connected the Canva account" sends people hunting for a bug in the folder.
 * This is the one place that decides which of the two real failures the
 * operator is looking at, and states the exact fix for each. It is pure and
 * standalone (no `lib/api.js`, so no `import.meta`) precisely so
 * `verify-studio.mjs` can assert the wording rather than trust it.
 */

/**
 * @param {{configured?:boolean, connected?:boolean, demo?:boolean, unreachable?:boolean}|null} status
 * @returns {{state:string, headline:string, fix:string, canConnect:boolean}|null}
 *          null when there is nothing wrong — live, or the badged demo tree.
 */
export function connectionGap(status) {
  if (!status) return null
  if (status.demo) return null
  if (status.connected) return null

  // THE PROBE ITSELF FAILED — which is a THIRD state, not the unconfigured one.
  // An expired session 401s `/api/canva/status`, and reading that as "the
  // backend has no Canva credentials" sends somebody to the Railway console to
  // fix a variable that was never missing. We only report what the backend
  // actually told us; when it told us nothing, we say so.
  if (status.unreachable) {
    return {
      state: 'unknown',
      headline: 'Could not ask the backend whether the Canva feed is connected.',
      fix: 'The status check did not answer — most often an expired sign-in. Sign in again and re-check before treating this as a backend configuration problem. Attaching or uploading a design works either way.',
      canConnect: false,
    }
  }

  if (status.configured) {
    return {
      state: 'disconnected',
      headline: 'The Canva account is not connected yet.',
      fix: 'The backend has its Canva app credentials but no authorized account. Connect once — the backend captures and stores the rotating refresh token itself, so no Canva credential ever reaches the browser.',
      canConnect: true,
    }
  }
  return {
    state: 'unconfigured',
    headline: 'The live Canva feed is not configured on the backend.',
    fix: 'sr-portal-backend needs CANVA_CLIENT_ID, CANVA_CLIENT_SECRET and CANVA_REFRESH_TOKEN in its Railway environment — a Canva Connect app with the folder:read and design:meta:read scopes. Until then every design must be attached or uploaded by hand, which works and is owned.',
    canConnect: false,
  }
}
